//@flow
import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import type { Node } from 'react';

const recurringMessages = {
    dayDuration: ({ daysCount }: { daysCount: number }) => (
        <FormattedMessage
            id="abt.loyaltyModules.childPrograms.childPrograms.dayDuration"
            defaultMessage="{daysCount} days"
            values={{ daysCount }}
        />
    ),
    dayDurationInfo: ({ daysCount }: { daysCount: number }) => (
        <FormattedMessage
            id="abt.loyaltyModules.childPrograms.dayDurationInfo"
            defaultMessage="You have an extra {daysCount} days to resolve any issues you may have with an order"
            values={{ daysCount }}
        />
    ),
};

recurringMessages.dayDuration.displayName = 'ChildProgramDayDuration';
recurringMessages.dayDurationInfo.displayName = 'ChildProgramDayDurationInfo';

type ChildProgram = {|
    displayName: Node,
    memberValue?: Node | boolean,
    bronzeValue?: Node | boolean,
    silverValue?: Node | boolean,
    goldValue?: Node | boolean,
    platinumValue?: Node | boolean,
    memberInfo?: Node,
    bronzeInfo?: Node,
    silverInfo?: Node,
    goldInfo?: Node,
    platinumInfo?: Node,
|};

type ProgramCategory = {|
    childPrograms: Array<ChildProgram>,
    displayLabel: Node,
    id: string,
|};

export const childPrograms: Array<ProgramCategory> = [
    {
        id: 'cash-rewards',
        displayLabel: (
            <FormattedMessage
                id="abt.loyaltyModules.childPrograms.childProgramConfig.displayLabelCashRewards"
                defaultMessage="Cash Rewards"
            />
        ),
        childPrograms: [
            {
                displayName: (
                    <FormattedMessage
                        id="abt.loyaltyModules.childPrograms.childProgramConfig.cashBackForPoints"
                        defaultMessage="Cash back for every {points} points"
                        values={{
                            points: <FormattedNumber value={5000} />,
                        }}
                    />
                ),
                memberValue: (
                    <FormattedNumber
                        currency="USD"
                        currencyDisplay="symbol"
                        maximumFractionDigits={0}
                        minimumFractionDigits={0}
                        style="currency"
                        value={50}
                    />
                ),
                bronzeValue: (
                    <FormattedNumber
                        currency="USD"
                        currencyDisplay="symbol"
                        maximumFractionDigits={0}
                        minimumFractionDigits={0}
                        style="currency"
                        value={50}
                    />
                ),
                silverValue: (
                    <FormattedNumber
                        currency="USD"
                        currencyDisplay="symbol"
                        maximumFractionDigits={0}
                        minimumFractionDigits={0}
                        style="currency"
                        value={50}
                    />
                ),
                goldValue: (
                    <FormattedNumber
                        currency="USD"
                        currencyDisplay="symbol"
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                        style="currency"
                        value={100}
                    />
                ),
                platinumValue: (
                    <FormattedNumber
                        currency="USD"
                        currencyDisplay="symbol"
                        maximumFractionDigits={0}
                        minimumFractionDigits={0}
                        style="currency"
                        value={125}
                    />
                ),
            },
        ],
    },
    {
        id: 'access',
        displayLabel: (
            <FormattedMessage
                id="abt.loyaltyModules.childPrograms.childProgramConfig.displayLabelAccess"
                defaultMessage="Insider Access"
            />
        ),
        childPrograms: [
            {
                displayName: (
                    <FormattedMessage
                        id="abt.loyaltyModules.childPrograms.childProgramConfig.sourcing"
                        defaultMessage="Sourcing Service"
                    />
                ),
                memberValue: true,
                bronzeValue: true,
                silverValue: true,
                goldValue: true,
                platinumValue: true,
            },
            {
                displayName: (
                    <FormattedMessage
                        id="abt.loyaltyModules.childPrograms.childProgramConfig.invitesToEvents"
                        defaultMessage="Exclusive 1stdibs Events"
                    />
                ),
                bronzeValue: true,
                silverValue: true,
                goldValue: true,
                platinumValue: true,
            },
            {
                displayName: (
                    <FormattedMessage
                        id="abt.loyaltyModules.childPrograms.childProgramConfig.vipEventAccess"
                        defaultMessage="VIP Experiences"
                    />
                ),
                goldValue: true,
                platinumValue: true,
            },
        ],
    },
    {
        id: 'branding',
        displayLabel: (
            <FormattedMessage
                id="abt.loyaltyModules.childPrograms.childProgramConfig.displayLabelBranding"
                defaultMessage="Branding"
            />
        ),
        childPrograms: [
            {
                displayName: (
                    <FormattedMessage
                        id="abt.loyaltyModules.childPrograms.childProgramConfig.publishedProfile"
                        defaultMessage="Published Profile"
                    />
                ),
                bronzeValue: true,
                silverValue: true,
                goldValue: true,
                platinumValue: true,
            },
            {
                displayName: (
                    <FormattedMessage
                        id="abt.loyaltyModules.childPrograms.childProgramConfig.designerConnection"
                        defaultMessage="Designer Connection"
                    />
                ),
                bronzeValue: true,
                silverValue: true,
                goldValue: true,
                platinumValue: true,
            },
            {
                displayName: (
                    <FormattedMessage
                        id="abt.loyaltyModules.childPrograms.childProgramConfig.spotlightMediaPackage"
                        defaultMessage="360&deg; Firm Feature"
                    />
                ),
                goldValue: true,
                platinumValue: true,
            },
        ],
    },
    {
        id: 'editorial',
        displayLabel: (
            <FormattedMessage
                id="abt.loyaltyModules.childPrograms.childProgramConfig.displayLabelEditorial"
                defaultMessage="Editorial*"
            />
        ),
        childPrograms: [
            {
                displayName: (
                    <FormattedMessage
                        id="abt.loyaltyModules.childPrograms.childProgramConfig.socialPost"
                        defaultMessage="Social Post"
                    />
                ),
                bronzeValue: false,
                silverValue: true,
                goldValue: true,
                platinumValue: true,
            },
            {
                displayName: (
                    <FormattedMessage
                        id="abt.loyaltyModules.childPrograms.childProgramConfig.editorialFeature"
                        defaultMessage="Editorial Coverage"
                    />
                ),
                goldValue: true,
                platinumValue: true,
            },
            {
                displayName: (
                    <FormattedMessage
                        id="abt.loyaltyModules.childPrograms.childProgramConfig.roomsOfDistinction"
                        defaultMessage="Print Feature"
                    />
                ),
                platinumValue: true,
            },
        ],
    },
    {
        id: 'peace-of-mind',
        displayLabel: (
            <FormattedMessage
                id="abt.loyaltyModules.childPrograms.childProgramConfig.displayLabelPeaceOfMind"
                defaultMessage="Peace of Mind"
            />
        ),
        childPrograms: [
            {
                displayName: (
                    <FormattedMessage
                        id="abt.loyaltyModules.childPrograms.childProgramConfig.enhancedBuyerProtection"
                        defaultMessage="Enhanced Buyer Protection"
                    />
                ),
                silverValue: recurringMessages.dayDuration({ daysCount: 30 }),
                goldValue: recurringMessages.dayDuration({ daysCount: 60 }),
                platinumValue: recurringMessages.dayDuration({ daysCount: 90 }),
                silverInfo: recurringMessages.dayDurationInfo({ daysCount: 30 }),
                goldInfo: recurringMessages.dayDurationInfo({ daysCount: 60 }),
                platinumInfo: recurringMessages.dayDurationInfo({ daysCount: 90 }),
            },
            {
                displayName: (
                    <FormattedMessage
                        id="abt.loyaltyModules.childPrograms.childProgramConfig.additionalSellerInfo"
                        defaultMessage="Additional Seller Information"
                    />
                ),
                goldValue: true,
                platinumValue: true,
            },
        ],
    },
];
